import { useState } from 'react'
import {
  ArrowDown,
  ArrowRight,
  Check,
  Download,
  ExternalLink,
  Github,
  Instagram,
  Menu,
  Moon,
  ShieldCheck,
  SlidersHorizontal,
  Sun,
  Upload,
  X,
  Zap,
} from 'lucide-react'
import { Logo } from './components/Logo'
import { WorkbenchIcon } from './components/WorkbenchIcon'
import { Workspace } from './components/Workspace'
import { tools } from './data/tools'
import type { ToolDefinition, WorkbenchGroup } from './types'

const groupDetails: Array<{
  name: WorkbenchGroup
  number: string
  id: string
  english: string
  description: string
}> = [
  { name: '줄이기', number: '01', id: 'bench-reduce', english: 'CUT IT DOWN', description: '크고 무거운 파일부터 정리한다.' },
  { name: '바꾸기', number: '02', id: 'bench-convert', english: 'SWITCH IT', description: '포맷과 방향을 필요한 모양으로 바꾼다.' },
  { name: '손보기', number: '03', id: 'bench-fix', english: 'TUNE IT UP', description: '색과 문구를 필요한 만큼만 더한다.' },
  { name: '지우기', number: '04', id: 'bench-clear', english: 'WIPE IT', description: '보이면 안 되는 것과 배경을 걷어낸다.' },
]

function App() {
  const [theme, setTheme] = useState<'dark' | 'light'>(() =>
    document.documentElement.dataset.theme === 'light' ? 'light' : 'dark',
  )
  const [activeTool, setActiveTool] = useState<ToolDefinition | null>(null)
  const [mobileMenu, setMobileMenu] = useState(false)

  const toggleTheme = () => {
    const nextTheme = theme === 'dark' ? 'light' : 'dark'
    setTheme(nextTheme)
    document.documentElement.dataset.theme = nextTheme
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', nextTheme === 'dark' ? '#090909' : '#f7faff')
    localStorage.setItem('zozin-theme', nextTheme)
  }

  const openTool = (tool: ToolDefinition) => {
    setActiveTool(tool)
    setMobileMenu(false)
  }

  return (
    <>
      <div className="site-shell" id="top">
        <header className="site-header">
          <div className="header-inner">
            <Logo />
            <nav className="desktop-nav" aria-label="주요 메뉴">
              <a href="#tools">작업대</a>
              <a href="#rules">작업 원칙</a>
              <button type="button" onClick={() => openTool(tools[0])}>바로 시작 <ArrowRight size={14} /></button>
            </nav>
            <div className="header-actions">
              <span className="local-badge"><i /> LOCAL ONLY</span>
              <button type="button" className="theme-toggle" onClick={toggleTheme} aria-label={theme === 'dark' ? '라이트 모드 켜기' : '다크 모드 켜기'}>
                {theme === 'dark' ? <Moon size={17} /> : <Sun size={17} />}
              </button>
              <button type="button" className="mobile-menu-button" onClick={() => setMobileMenu((open) => !open)} aria-label="메뉴 열기">
                {mobileMenu ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
          {mobileMenu && (
            <nav className="mobile-menu" aria-label="모바일 메뉴">
              {groupDetails.map((group) => <a key={group.id} href={`#${group.id}`} onClick={() => setMobileMenu(false)}>{group.number} / {group.name}<ArrowRight size={15} /></a>)}
              <a href="#rules" onClick={() => setMobileMenu(false)}>작업 원칙<ArrowRight size={15} /></a>
            </nav>
          )}
        </header>

        <main>
          <section className="bench-hero">
            <div className="hero-coordinate coordinate-top">SEOUL / 37.5665° N</div>
            <div className="hero-coordinate coordinate-side">IMAGE WORKBENCH · EST. 2026</div>
            <div className="bench-hero-copy">
              <div className="work-label"><span>OPEN</span> zozinimg 작업대</div>
              <h1><span>이미지,</span><br />여기서 끝내자.</h1>
              <p>가볍게 만들고, 필요한 만큼만 남긴다.<br />파일은 서버로 안 보낸다.</p>
              <div className="hero-actions">
                <button type="button" className="primary-button hero-primary" onClick={() => openTool(tools[0])}>
                  <Zap size={19} fill="currentColor" /> 작업대 열기
                </button>
                <a href="#tools" className="text-button">공구부터 보기 <ArrowDown size={17} /></a>
              </div>
              <dl className="hero-specs">
                <div><dt>공구</dt><dd>{tools.length}개</dd></div>
                <div><dt>업로드</dt><dd>0 byte</dd></div>
                <div><dt>결과</dt><dd>바로 저장</dd></div>
              </dl>
            </div>

            <div className="workbench-machine" aria-label="브라우저 안에서 이미지를 처리하는 zozinimg 작업대 모형">
              <div className="machine-topbar">
                <span><i /> BENCH READY</span>
                <b>NO. 001</b>
              </div>
              <div className="machine-stage">
                <span className="tape tape-one">원본 8.4 MB</span>
                <span className="tape tape-two">-85%</span>
                <div className="sample-sheet sheet-back" />
                <div className="sample-sheet sheet-front">
                  <div className="sample-image">
                    <span className="sample-sun" />
                    <i className="sample-line line-one" />
                    <i className="sample-line line-two" />
                    <i className="sample-ground ground-one" />
                    <i className="sample-ground ground-two" />
                  </div>
                  <div className="sample-meta"><span>summer-trip.jpg</span><b>1.2 MB</b></div>
                </div>
                <svg className="marker-arrow" viewBox="0 0 140 90" aria-hidden="true"><path d="M8 73c35 8 73-5 101-38" /><path d="m93 34 18-2-3 18" /></svg>
              </div>
              <div className="machine-controls">
                <span><b>01</b> 파일 넣기</span>
                <i />
                <span><b>02</b> 설정 맞추기</span>
                <i />
                <span><b>03</b> 결과 받기</span>
              </div>
            </div>
          </section>

          <section className="workbench-section" id="tools">
            <header className="bench-heading">
              <div>
                <span className="section-index">[ WORKBENCH / {String(tools.length).padStart(2, '0')} TOOLS ]</span>
                <h2>무엇을 손볼까.</h2>
              </div>
              <p>도구부터 고르면 된다.<br />설명보다 결과가 먼저다.</p>
            </header>

            <nav className="drawer-index" aria-label="작업 서랍 바로가기">
              {groupDetails.map((group) => (
                <a href={`#${group.id}`} key={group.id}><span>{group.number}</span><strong>{group.name}</strong><small>{group.english}</small></a>
              ))}
            </nav>

            <div className="workbench-bays">
              {groupDetails.map((group) => {
                const groupTools = tools.filter((tool) => tool.workbenchGroup === group.name)
                return (
                  <section className="tool-bay" id={group.id} key={group.id}>
                    <header className="tool-bay-heading">
                      <div className="bay-number">{group.number}</div>
                      <div><span>{group.english}</span><h3>{group.name}</h3><p>{group.description}</p></div>
                      <ArrowRight className="bay-arrow" size={28} />
                    </header>
                    <div className={`tool-drawer tool-count-${groupTools.length}`}>
                      {groupTools.map((tool) => {
                        const toolNumber = String(tools.findIndex((candidate) => candidate.id === tool.id) + 1).padStart(2, '0')
                        return (
                          <button type="button" className="tool-slot" key={tool.id} onClick={() => openTool(tool)}>
                            <div className="tool-slot-top"><span>TOOL {toolNumber}</span>{tool.badge && <b>{tool.badge}</b>}</div>
                            <div className="tool-glyph"><WorkbenchIcon id={tool.id} /></div>
                            <div className="tool-slot-copy"><h4>{tool.title}</h4><p>{tool.description}</p></div>
                            <div className="tool-slot-action"><span>{tool.action}</span><ArrowRight size={18} /></div>
                          </button>
                        )
                      })}
                    </div>
                  </section>
                )
              })}
            </div>
          </section>

          <section className="rules-section" id="rules">
            <div className="rules-stamp" aria-hidden="true"><ShieldCheck size={52} /><span>LOCAL<br />ONLY</span></div>
            <div className="rules-copy">
              <span className="section-index">[ 작업대 규칙 / 딱 세 가지 ]</span>
              <h2>파일은 밖으로<br />안 나간다.</h2>
              <p>선택한 이미지는 지금 쓰는 브라우저 안에서 처리한다. 회원가입도, 서버 보관도 없다. 끝난 결과만 직접 가져가면 된다.</p>
            </div>
            <div className="rules-list">
              <article><span>01</span><ShieldCheck /><div><h3>서버 안 거침</h3><p>원본 파일을 외부 저장소로 보내지 않는다.</p></div></article>
              <article><span>02</span><SlidersHorizontal /><div><h3>설정은 직접 확인</h3><p>자동인 척하지 않는다. 옵션과 한계를 보여준다.</p></div></article>
              <article><span>03</span><Download /><div><h3>결과만 가져가기</h3><p>한 장은 바로, 여러 장은 ZIP으로 받는다.</p></div></article>
            </div>
            <div className="local-flow" aria-label="로컬 이미지 처리 순서">
              <span><Upload size={19} /> 내 파일</span><i>→</i><strong>브라우저 작업대</strong><i>→</i><span><Download size={19} /> 결과 파일</span>
              <em>SERVER</em>
            </div>
          </section>

          <section className="bench-callout">
            <span><Check size={16} /> 설명 끝.</span>
            <h2>이제 작업하면 된다.</h2>
            <button type="button" onClick={() => openTool(tools[0])}>첫 파일 손보기 <ArrowRight size={19} /></button>
          </section>
        </main>

        <footer className="site-footer">
          <div className="footer-main">
            <div className="footer-brand">
              <Logo />
              <p>이미지를 밖으로 보내지 않고<br />브라우저 안에서 끝내는 작업대.</p>
              <span><ShieldCheck size={15} /> BUILT BY zozindev</span>
            </div>
            <div className="footer-links">
              <div>
                <strong>작업 서랍</strong>
                {groupDetails.map((group) => <a href={`#${group.id}`} key={group.id}>{group.number} {group.name}</a>)}
              </div>
              <div>
                <strong>zozindev</strong>
                <a href="https://zozindev.com" target="_blank" rel="noreferrer">개인 블로그 <ExternalLink size={13} /></a>
                <a href="https://github.com/zozindev/zozinpdf" target="_blank" rel="noreferrer">GitHub <Github size={14} /></a>
                <a href="https://www.instagram.com/zozindev/" target="_blank" rel="noreferrer">Instagram <Instagram size={14} /></a>
              </div>
              <div>
                <strong>프로젝트</strong>
                <a href="#rules">작업 원칙</a>
                <a href="mailto:hello@zozindev.com">문의하기</a>
                <a href="https://github.com/zozindev/zozinimg" target="_blank" rel="noreferrer">소스 보기 <ExternalLink size={13} /></a>
              </div>
            </div>
          </div>
          <div className="footer-bottom"><span>© {new Date().getFullYear()} zozindev</span><span>SEOUL, KR <i /> CLIENT-SIDE IMAGE WORKBENCH</span></div>
        </footer>
      </div>

      {activeTool && <Workspace tool={activeTool} onClose={() => setActiveTool(null)} />}
    </>
  )
}

export default App
